import { useApolloClient } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'
import { GET_ALL_COUNTRIES_LIST } from '../graphql/queries'
import { CountriesListProps } from '@interflora/ui-components/build/common/props'

/**
 * A simplified hook to fetch countries list only when needed
 */
export const useCountriesList = (
  options: {
    skip?: boolean
    onlyFetchWhen?: boolean
  } = {}
) => {
  const { skip = false, onlyFetchWhen = true } = options
  const [countriesList, setCountriesList] = useState<CountriesListProps>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const client = useApolloClient()
  const hasFetched = useRef(false)
  const isQueryingRef = useRef(false)

  useEffect(() => {
    if (skip || !onlyFetchWhen || hasFetched.current || isQueryingRef.current || !client) {
      return
    }

    // Mark as querying to prevent duplicate requests
    isQueryingRef.current = true
    setLoading(true)

    // Simple query with no-cache policy to avoid retries
    client
      .query({
        query: GET_ALL_COUNTRIES_LIST,
        fetchPolicy: 'no-cache',
      })
      .then((result) => {
        if (result?.data?.getAllCountriesList?.countriesList) {
          setCountriesList(result.data.getAllCountriesList.countriesList)
          hasFetched.current = true
        }
      })
      .catch((err) => {
        console.error('Error fetching countries list:', err)
        setError(err instanceof Error ? err : new Error('Failed to fetch countries list'))
      })
      .finally(() => {
        setLoading(false)
        isQueryingRef.current = false
      })

    // Clean up function
    return () => {
      // Not needed for this specific implementation
    }
  }, [skip, onlyFetchWhen, client])

  // Reset the fetched state if onlyFetchWhen changes to false
  useEffect(() => {
    if (!onlyFetchWhen) {
      hasFetched.current = false
    }
  }, [onlyFetchWhen])

  return {
    countriesList,
    loading,
    error,
  }
}

export default useCountriesList
